import { consistentString } from '../../utils/content-formatting';
import { addSubPx } from './utils';
import { getFocusStylesWithBorder } from './focus';

const buttonsColorMap = {
  primary: {
    backgroundColor: 'primaryLink',
    border: null,
    color: 'white',
    backgroundColorHover: 'primaryLinkHover',
    borderHover: null,
    colorHover: 'white',
  },
  secondaryOutline: {
    backgroundColor: 'white',
    border: 'secondaryButton',
    color: 'secondaryButton',
    backgroundColorHover: 'white',
    borderHover: 'primaryLink',
    colorHover: 'primaryLink',
  },
  secondaryStandard: {
    backgroundColor: 'darkBackground',
    border: null,
    color: 'white',
    backgroundColorHover: 'primaryLink',
    borderHover: null,
    colorHover: 'white',
  },
  secondaryOnDark: {
    backgroundColor: 'transparent',
    border: 'white',
    color: 'white',
    backgroundColorHover: 'transparent',
    borderHover: 'linkHoverOnDarkBG',
    colorHover: 'linkHoverOnDarkBG',
  },
  disabled: {
    backgroundColor: 'disabled',
    border: null,
    color: 'white',
    backgroundColorHover: null,
    borderHover: null,
    colorHover: 'white',
  },
  default: {
    backgroundColor: null,
    border: null,
    color: null,
    backgroundColorHover: null,
    borderHover: null,
    colorHover: null,
  },
};

export const translateBG = ($bg) => {
  const map = {
    primary: 'primary',
    'secondary-standard': 'secondaryStandard',
    'secondary-outline': 'secondaryOutline',
    'secondary-on-dark': 'secondaryOnDark',
    default: 'default',
  };
  return map[consistentString($bg)];
};

export const getBackgroundColor = ({ theme, $bg, disabled }) => {
  if (!$bg) return;
  const col = disabled ? 'disabled' : translateBG($bg);
  if (!col) return;
  return theme.palette[buttonsColorMap[col].backgroundColor];
};

export const getBackgroundColorHover = ({ theme, $bg, disabled }) => {
  if (!$bg) return;
  const col = disabled ? 'disabled' : translateBG($bg);
  return theme.palette[buttonsColorMap[col].backgroundColorHover];
};

export const getColor = ({ theme, $bg, disabled }) => {
  if (!$bg) return;
  const col = disabled ? 'disabled' : translateBG($bg);
  return theme.palette[buttonsColorMap[col].color];
};

export const getColorHover = ({ theme, $bg, disabled }) => {
  if (!$bg) return;
  const col = disabled ? 'disabled' : translateBG($bg);
  return theme.palette[buttonsColorMap[col].colorHover];
};

export const getBorder = ({ theme, $bg, disabled }) => {
  if (!$bg) return 'none';
  const col = disabled ? 'disabled' : translateBG($bg);
  return buttonsColorMap[col].border
    ? `inset 0 0 0 2px ${theme.palette[buttonsColorMap[col].border]}`
    : 'none';
};

export const getBorderHover = ({ theme, $bg, disabled }) => {
  if (!$bg) return;
  const col = disabled ? 'disabled' : translateBG($bg);
  return buttonsColorMap[col].borderHover
    ? `inset 0 0 0 2px ${theme.palette[buttonsColorMap[col].borderHover]}`
    : 'none';
};

export const getPadding = ({ theme, $iconPlacement, $size }) => {
  // center placement for a square icon button, requires custom padding
  if ($iconPlacement === 'center') {
    switch ($size) {
      case 'small':
        // 7px 11px 8px
        return `
          ${addSubPx(-1)(theme.spacing.tiny)}
          ${addSubPx(-1)(theme.spacing.small)}
          ${theme.spacing.tiny}`;

      case 'xl':
        // 17px 18px 18px
        return `
          ${addSubPx(1)(theme.spacing.standard)}
          ${addSubPx(2)(theme.spacing.standard)}
          ${addSubPx(2)(theme.spacing.standard)}`;

      case 'xxl':
        // 21px 20px
        return `
          ${addSubPx(-3)(theme.spacing.medium)}
          ${addSubPx(-4)(theme.spacing.medium)}`;

      default:
        // size: regular
        // 11px 15px 12px
        return `
          ${addSubPx(-1)(theme.spacing.small)}
          ${addSubPx(-1)(theme.spacing.standard)}
          ${theme.spacing.small}`;
    }
  }

  // non centered icon rules
  switch ($size) {
    case 'small':
      // 7px 8px 8px
      return `
        ${addSubPx(-1)(theme.spacing.tiny)}
        ${theme.spacing.tiny}
        ${theme.spacing.tiny}`;

    case 'xl':
      // 17px 16px 18px
      return `
        ${addSubPx(-1)(theme.spacing.standard)}
        ${theme.spacing.standard}
        ${addSubPx(2)(theme.spacing.standard)}`;

    case 'xxl':
      // 20px 24px 19px
      return `
        ${addSubPx(-4)(theme.spacing.medium)}
        ${theme.spacing.medium}
        ${addSubPx(-5)(theme.spacing.medium)}`;

    default:
      // size: regular
      // 11px 12px 12px
      return `
        ${addSubPx(-1)(theme.spacing.small)}
        ${theme.spacing.small}
        ${theme.spacing.small}`;
  }
};

export const getIconPadding = ({ theme, $size = 'regular' }) => {
  if ($size === 'small') return `${addSubPx(-3)(theme.spacing.tiny)}`; // 5px
  if ($size === 'xl') return `${theme.spacing.standard}`;
  if ($size === 'xxl') return `${theme.spacing.standard}`;
  return theme.spacing.small;
};

export const getIconSize = ({ theme, $size = 'regular' }) => {
  if ($size === 'xl') return `${theme.spacing.medium}`;
  if ($size === 'xxl') return `${theme.spacing.xlarge}`;
  return `${addSubPx(2)(theme.spacing.standard)}`; // 18px
};

export const getHeightWidth = ({ $size }) => {
  if ($size === 'small') return '40px';
  if ($size === 'regular') return '48px';
  if ($size === 'xl') return '60px';
  if ($size === 'xxl') return '80px';
};

export const getFocusStyles = ({ theme, $bg, disabled }) => {
  if (!$bg) return;
  const border = getBorderHover({ theme, $bg, disabled });

  return getFocusStylesWithBorder(border);
};
